import { defineStore } from 'pinia'
import { FetchError } from 'ofetch'
import { useI18n } from 'vue-i18n'
import {
  isSortTheSame,
  isCategoryTheSame,
  isQueryTheSame,
  FilterParams,
  setSort,
} from './params-helper'
import type { FilterItem } from '~/models/filter'
import { NavigationSortType } from '~/models/navigation'
import {
  type MarketplaceProduct,
  MarketplaceProductEntityType,
} from '~/models/marketplace'
import { getAbbrFromFullName } from '~/composables/use-users-getters'

const path = '/catalog/products'

type FilterResponse = { categories: FilterItem[] }

interface MarketplaceState {
  product: MarketplaceProduct | null
  isProductLoaded: boolean
  isFiltersLoading: boolean
  isCategoryChanging: boolean
  filters: null | FilterResponse
}

interface MarketplaceProductEntityStyle {
  icon: string
  labelKey: string
  getLink: (entityId: number) => string
  getAbbr: (entityName: string) => string
  getSize: (entitySize: number) => string
}

export const useMarketplace = defineStore('marketplace', () => {
  // TODO: https://app.asana.com/0/1202924432057564/1203829013202629
  const elasticURL = useRuntimeConfig().public.gatewayElastic

  const state = reactive<MarketplaceState>({
    product: null,
    isCategoryChanging: false,
    isProductLoaded: false,
    isFiltersLoading: false,
    filters: null,
  })

  const initProductsList = () => {
    const list = useInfinityList<MarketplaceProduct>(path, elasticURL)

    list.state.params = {
      per_page: 20,
    }

    return list
  }

  const productsList = initProductsList()

  async function fetchProduct(id: number) {
    if (state.product?.id === Number(id)) {
      return
    }

    if (isNaN(Number(id))) {
      throw useCreateScreenError({ message: 'product id should be a number' })
    }

    state.product = null

    state.isProductLoaded = false

    try {
      const { data } = await useAuthFetch<{ data: MarketplaceProduct }>(
        `${path}/${id}`,
        {
          baseURL: elasticURL,
        }
      )

      state.product = data
    } catch (error: any) {
      if ((error as FetchError).status === 404) {
        throw useCreateScreenError({ message: 'no such product' })
      } else {
        throw error
      }
    } finally {
      state.isProductLoaded = true
    }
  }

  async function fetchFilters(categories?: number[]) {
    try {
      state.isFiltersLoading = true
      const params = {
        [FilterParams.Categories]:
          categories || productsList.state.params[FilterParams.Categories],
      }
      const { data } = await useAuthFetch<{ data: FilterResponse }>(
        `${path}/filter`,
        {
          baseURL: elasticURL,
          params,
        }
      )
      data.categories = data.categories.map((category) => {
        category.name = parseFieldLocales(category.name as string) || ''
        return category
      })
      state.filters = data
    } finally {
      state.isFiltersLoading = false
    }
  }

  const resetProduct = () => {
    state.product = null
  }

  function resetProductsListWithFilters(
    sort: NavigationSortType,
    query?: string,
    categoryId?: number
  ) {
    if (
      isSortTheSame(productsList.state.params, sort) &&
      isCategoryTheSame(productsList.state.params, categoryId) &&
      isQueryTheSame(productsList.state.params, query)
    ) {
      return
    }

    setSort(productsList.state.params, sort)

    if (categoryId) {
      productsList.state.params[FilterParams.Categories] = [categoryId]
    } else {
      delete productsList.state.params[FilterParams.Categories]
    }

    if (query) {
      productsList.state.params[FilterParams.Query] = query
    } else {
      delete productsList.state.params[FilterParams.Query]
    }

    delete productsList.state.params[FilterParams.EventArray]

    productsList.reset()
  }

  const isListHasFilters = computed(() => {
    return productsList.state.params[FilterParams.Query]
  })

  // const updateProductsList = async (updateFilters = true) => {
  //   if (updateFilters) {
  //     process.client && (state.isCategoryChanging = true)
  //     await Promise.all([productsList.getPage(), fetchFilters()])
  //     state.isCategoryChanging = false
  //   } else {
  //     await productsList.getPage()
  //   }
  // }

  const updateProductsList = async () => {
    await productsList.getPage()
  }

  const EntityStyleMap: Record<
    MarketplaceProductEntityType,
    MarketplaceProductEntityStyle
  > = {
    [MarketplaceProductEntityType.User]: {
      icon: 'outline_user',
      labelKey: 'marketplace.freelancer',
      getLink: (entityId) => `/users/${entityId}`,
      getAbbr: (entityName) => getAbbrFromFullName(entityName),
      getSize: () => '',
    },
    [MarketplaceProductEntityType.Company]: {
      icon: 'outline_briefcase',
      labelKey: 'marketplace.company',
      getLink: (entityId) => `/companies/${entityId}`,
      getAbbr: (entityName) => entityName.slice(0, 2),
      getSize: (entitySize) => {
        const catalogs = useCatalog()
        const i18n = useI18n()

        const size = catalogs.companySizes.find(
          (item) => item.value === entitySize
        )?.label
        return size ? size + ' ' + i18n.t('marketplace.employees') : ''
      },
    },
  }

  return {
    product: toRef(state, 'product'),
    isProductLoaded: toRef(state, 'isProductLoaded'),
    isFiltersLoading: toRef(state, 'isFiltersLoading'),
    isCategoryChanging: toRef(state, 'isCategoryChanging'),
    list: productsList.state,
    filters: toRef(state, 'filters'),
    fetchFilters,
    fetchProduct,
    resetProduct,
    allRecordsLoaded: productsList.allRecordsLoaded,
    fetch: productsList.fetch,
    getProductsList: updateProductsList,
    getPage: updateProductsList,
    nextPage: productsList.nextPage,
    resetProductsListWithFilters,
    isListHasFilters,
    EntityStyleMap,
  }
})
